/**
 * Fabrique de clés de requête TanStack Query — convention documentée dans
 * `apps/web/README-shell.md`. Chaque famille reprend le segment `/api/*`
 * correspondant ; les listes filtrées ajoutent les paramètres normalisés en
 * dernier élément pour que `invalidateQueries({queryKey: queryKeys.orders.all})`
 * invalide toutes les variantes d'une famille sans lister chaque filtre.
 *
 * **Toutes** les familles vivent ici : un écran n'écrit pas ses propres
 * tableaux littéraux et ne redéclare pas de fabrique locale.
 */
export const queryKeys = {
  auth: {
    me: () => ['auth', 'me'] as const,
  },
  settings: {
    maintenance: () => ['settings', 'maintenance'] as const,
  },
  stores: {
    all: ['stores'] as const,
    list: () => ['stores', 'list'] as const,
  },
  orders: {
    all: ['orders'] as const,
    /** Liste unifiée (commandes Shopify + factures Pennylane). */
    unified: (query: Record<string, unknown>) => ['orders', 'unified', query] as const,
    list: (query: Record<string, unknown>) => ['orders', 'list', query] as const,
    detail: (id: string) => ['orders', 'detail', id] as const,
    csv: (id: string, fileId: string | undefined) => ['orders', 'csv', id, fileId ?? null] as const,
    files: (id: string) => ['orders', 'files', id] as const,
    operationsOptions: () => ['orders', 'operations-options'] as const,
    attentionCount: () => ['orders', 'attention-count'] as const,
  },
  invoices: {
    all: ['invoices'] as const,
    detail: (id: string) => ['invoices', 'detail', id] as const,
  },
  runs: {
    all: ['runs'] as const,
    list: (query: Record<string, unknown>) => ['runs', 'list', query] as const,
    fulfillmentDetail: (id: string) => ['runs', 'fulfillment', id] as const,
    pennylaneDetail: (id: string) => ['runs', 'pennylane', id] as const,
    errorLines: (query: Record<string, unknown>) => ['runs', 'error-lines', query] as const,
  },
  dashboard: {
    all: ['dashboard'] as const,
    payload: (period?: { from: string; to: string }) => ['dashboard', 'payload', period ?? null] as const,
  },
  operations: {
    all: ['operations'] as const,
    list: (query: Record<string, unknown>) => ['operations', 'list', query] as const,
    detail: (id: string) => ['operations', 'detail', id] as const,
    orders: (id: string, query: Record<string, unknown>) => ['operations', 'orders', id, query] as const,
    renewalRun: (id: string, runId: string) => ['operations', 'renewal-run', id, runId] as const,
    renewalApplications: (id: string, query: Record<string, unknown>) =>
      ['operations', 'renewal-applications', id, query] as const,
    renewalApplication: (id: string, applicationId: string) =>
      ['operations', 'renewal-application', id, applicationId] as const,
  },
  journal: {
    all: ['journal'] as const,
    list: (query: Record<string, unknown>) => ['journal', 'list', query] as const,
  },
  bundles: {
    all: ['bundles'] as const,
    list: (query: Record<string, unknown>) => ['bundles', 'list', query] as const,
    detail: (sku: string) => ['bundles', 'detail', sku] as const,
  },
  carriers: {
    all: ['carriers'] as const,
    /** Liste globale et ordonnée : une seule entrée, pas de variante par boutique. */
    rules: () => ['carriers', 'rules'] as const,
    defaults: () => ['carriers', 'defaults'] as const,
    coverage: (store: string) => ['carriers', 'coverage', store] as const,
  },
  /** Paramètres → Recharge (`06` §7, §1.3) : réglages et webhooks enregistrés chez Recharge. */
  rechargeSettings: {
    all: ['recharge-settings'] as const,
    summary: () => ['recharge-settings', 'summary'] as const,
    webhooks: () => ['recharge-settings', 'webhooks'] as const,
  },
  /** Paramètres → Klaviyo (`06` §11.4) : intégration partagée par les fonctions qui l'utilisent. */
  klaviyoSettings: {
    all: ['klaviyo-settings'] as const,
    summary: () => ['klaviyo-settings', 'summary'] as const,
  },
  /** Écran Abonnements (`06` §8) : journaux et listes lus sous `/api/recharge/*`. */
  subscriptions: {
    all: ['subscriptions'] as const,
    webhookEvents: (query: Record<string, unknown>) =>
      ['subscriptions', 'webhook-events', 'list', query] as const,
    webhookEvent: (id: string) => ['subscriptions', 'webhook-events', 'detail', id] as const,
    chargeFailures: (query: Record<string, unknown>) =>
      ['subscriptions', 'charge-failures', 'list', query] as const,
    chargeFailure: (id: string) => ['subscriptions', 'charge-failures', 'detail', id] as const,
    /** Toutes les pages de l'onglet Exports (invalidation après « Générer l'export »). */
    exportsAll: ['subscriptions', 'exports'] as const,
    exports: (query: Record<string, unknown>) => ['subscriptions', 'exports', 'list', query] as const,
    /** Listes, détails et synthèse de l'onglet Cantons suisses (invalidation après une vérification). */
    cantonFixesAll: ['subscriptions', 'canton-fixes'] as const,
    cantonFixes: (query: Record<string, unknown>) =>
      ['subscriptions', 'canton-fixes', 'list', query] as const,
    cantonFix: (id: string) => ['subscriptions', 'canton-fixes', 'detail', id] as const,
    cantonFixesOverview: () => ['subscriptions', 'canton-fixes', 'overview'] as const,
    migrations: (query: Record<string, unknown>) =>
      ['subscriptions', 'migrations', 'list', query] as const,
    migration: (id: string) => ['subscriptions', 'migrations', 'detail', id] as const,
    /** Toutes les pages et détails de l'onglet Klaviyo (invalidation après un lancement). */
    klaviyoSyncsAll: ['subscriptions', 'klaviyo-syncs'] as const,
    klaviyoSyncs: (query: Record<string, unknown>) =>
      ['subscriptions', 'klaviyo-syncs', 'list', query] as const,
    klaviyoSync: (id: string) => ['subscriptions', 'klaviyo-syncs', 'detail', id] as const,
  },
} as const;
